import { ReactNode } from 'react'
import { TASKS_MODULE_NAME } from '@common/constants/task'
import { AI_BRAND_NAME } from '@common/interfaces/atlasai'
import { UserRole } from '@common/interfaces/user'
import AtlasAIIconActive from '../../assets/images/ai-robot-active.svg?react'
import AtlasAIIcon from '../../assets/images/ai-robot.svg?react'
import ExportsIcon from '../../assets/images/download-alt-icon.svg?react'
import ClientSettings from '../../assets/images/settings-icon2.svg?react'
import { ROUTES } from '../../constants/routes'
import Icon from '../Common/Icon'
import styles from './SideMenu.module.scss'

/**
 * Live counter to show on a menu item. Declared here rather than baked into the
 * item as a number because the config is a static module-level array — the count
 * is redux state, resolved at render time in SideMenu.
 */
export type MenuBadge = 'unreadMessages' | 'unreadTasks'

export interface IMenuData {
  label: string
  icon: ReactNode
  link: string
  key: string
  activeIcon?: ReactNode
  subItems?: ISubMenuData[]
  divider?: boolean
  badge?: MenuBadge
  // Extra routes that should still light this item up. Needed where `link` is a
  // landing route that immediately forwards elsewhere, or where one item covers
  // a family of sibling routes — NavLink's own `isActive` only knows `link`.
  activeRoutes?: string[]
}

export interface ISubMenuData {
  label: string
  link: string
  icon?: ReactNode
}

const MY_TASKS_MENU_ITEM: IMenuData = {
  label: TASKS_MODULE_NAME,
  icon: <Icon name="check-circle" size="md" />,
  // SideMenu overrides `link` with the tab this browser was last on; this is the
  // fallback, and `key` is what it matches to find this item.
  link: ROUTES.MY_TASKS,
  key: ROUTES.MY_TASKS,
  badge: 'unreadTasks',
  // Every tab of ACT, including a single project's — otherwise the
  // item goes dark the moment you leave My Tasks, which it already did for
  // Projects and Custom Fields before this list existed.
  activeRoutes: [
    ROUTES.MY_TASKS,
    ROUTES.TASK_PROJECTS,
    ROUTES.TASK_PROJECT_PAGE,
    ROUTES.CUSTOM_FIELDS,
  ],
}

// Internal-only surface: team messaging is not exposed to client or participant
// users, so this item is added to the internal role menus only.
const MESSAGES_MENU_ITEM: IMenuData = {
  label: 'AIM',
  icon: <Icon name="chat-notification" size="md" />,
  link: ROUTES.MESSAGES,
  key: ROUTES.MESSAGES,
  badge: 'unreadMessages',
}

const ERROR_CENTER_MENU_ITEM: IMenuData = {
  label: 'Error Center',
  icon: <Icon name="alert-triangle" size="md" />,
  link: ROUTES.ERROR_CENTER,
  key: ROUTES.ERROR_CENTER,
}

// Report Builder: saved, editable reports over the pipeline and referral sources.
const REPORTS_MENU_ITEM: IMenuData = {
  label: 'Reports',
  icon: <Icon name="chart-bars" size="md" />,
  link: ROUTES.REPORTS,
  key: ROUTES.REPORTS,
  activeRoutes: [ROUTES.REPORTS, ROUTES.REPORT_PAGE],
}

const SIDE_MENU_ADMIN: IMenuData[] = [
  {
    label: 'Clients',
    icon: <Icon name="people" size="md" />,
    link: ROUTES.CLIENT_MANAGEMENT,
    key: ROUTES.CLIENT_MANAGEMENT,
    activeIcon: <Icon name="people-active" size="md" />,
  },
  MY_TASKS_MENU_ITEM,
  MESSAGES_MENU_ITEM,
  {
    label: 'Collections',
    link: ROUTES.COLLECTIONS_DASHBOARD,
    key: ROUTES.COLLECTIONS_DASHBOARD,
    icon: <Icon name="collections" size="md" />,
  },
  {
    label: 'Operations',
    icon: <Icon name="activity-box" size="md" />,
    key: ROUTES.ACTIVITY_QUEUE,
    link: ROUTES.ACTIVITY_QUEUE,
  },
  {
    label: 'Wires',
    icon: <Icon name="transfer" size="md" />,
    key: ROUTES.OUTGOING_WIRE,
    link: ROUTES.OUTGOING_WIRE,
  },
  {
    label: 'Exports',
    icon: <ExportsIcon />,
    key: ROUTES.EXPORTS_PAGE,
    link: ROUTES.EXPORTS_PAGE,
    activeIcon: <ExportsIcon className={styles.exportIcon} />,
  },
  {
    divider: true,
    label: 'CRM',
    icon: <Icon name="briefcase" size="md" />,
    key: ROUTES.CRM_HOME,
    link: ROUTES.CRM_HOME,
  },
  {
    label: 'Participants',
    icon: <Icon name="pie-chart" size="md" />,
    key: ROUTES.PARTICIPANTS_MANAGEMENT,
    link: ROUTES.PARTICIPANTS_MANAGEMENT,
    activeIcon: <Icon name="pie-chart-active" size="md" />,
  },
  {
    label: 'Prospects',
    icon: <Icon name="prospects" size="md" />,
    key: ROUTES.PROSPECTS,
    link: ROUTES.PROSPECTS,
  },
  REPORTS_MENU_ITEM,
  {
    label: 'Diligence',
    icon: <Icon name="underwriting" size="md" />,
    key: ROUTES.DUE_DILIGENCE_HOME,
    link: ROUTES.DUE_DILIGENCE_HOME,
  },
  {
    divider: true,
    label: 'Users',
    icon: <Icon name="user-management" size="md" />,
    key: ROUTES.USER_MANAGEMENT,
    link: ROUTES.USER_MANAGEMENT,
    activeIcon: <Icon name="user-management-active" size="md" />,
  },
  {
    label: 'Audit Log',
    icon: <Icon name="audit-log" size="md" />,
    key: ROUTES.AUDIT,
    link: ROUTES.AUDIT,
    activeIcon: <Icon name="audit-log" size="md" />,
  },
  ERROR_CENTER_MENU_ITEM,
  {
    divider: true,
    label: AI_BRAND_NAME,
    icon: <AtlasAIIcon />,
    activeIcon: <AtlasAIIconActive />,
    key: ROUTES.ATLAS_AI,
    link: ROUTES.ATLAS_AI,
  },
]
export const SIDE_MENU_PORTFOLIO_USER: IMenuData[] = [
  {
    label: 'Dashboard',
    icon: <Icon name="dashboard" size="md" />,
    link: ROUTES.CLIENT_MANAGEMENT,
    key: ROUTES.CLIENT_MANAGEMENT,
  },
  MY_TASKS_MENU_ITEM,
  MESSAGES_MENU_ITEM,
  {
    label: 'Collections',
    link: ROUTES.COLLECTIONS_DASHBOARD,
    key: ROUTES.COLLECTIONS_DASHBOARD,
    icon: <Icon name="collections" size="md" />,
  },
  {
    label: 'Operations',
    icon: <Icon name="activity-box" size="md" />,
    key: ROUTES.ACTIVITY_QUEUE,
    link: ROUTES.ACTIVITY_QUEUE,
  },
  {
    label: 'Wires',
    icon: <Icon name="transfer" size="md" />,
    key: ROUTES.OUTGOING_WIRE,
    link: ROUTES.OUTGOING_WIRE,
  },
  {
    label: 'Exports',
    icon: <ExportsIcon />,
    key: ROUTES.EXPORTS_PAGE,
    link: ROUTES.EXPORTS_PAGE,
    activeIcon: <ExportsIcon className={styles.exportIcon} />,
  },
  {
    divider: true,
    label: 'CRM',
    icon: <Icon name="briefcase" size="md" />,
    key: ROUTES.CRM_HOME,
    link: ROUTES.CRM_HOME,
  },
  {
    label: 'Participants',
    icon: <Icon name="pie-chart" size="md" />,
    key: ROUTES.PARTICIPANTS_MANAGEMENT,
    link: ROUTES.PARTICIPANTS_MANAGEMENT,
    activeIcon: <Icon name="pie-chart-active" size="md" />,
  },
  {
    label: 'Prospects',
    icon: <Icon name="prospects" size="md" />,
    key: ROUTES.PROSPECTS,
    link: ROUTES.PROSPECTS,
  },
  REPORTS_MENU_ITEM,
  {
    label: 'Diligence',
    icon: <Icon name="underwriting" size="md" />,
    key: ROUTES.DUE_DILIGENCE_HOME,
    link: ROUTES.DUE_DILIGENCE_HOME,
  },
  {
    label: 'Audit Log',
    icon: <Icon name="audit-log" size="md" />,
    key: ROUTES.AUDIT,
    link: ROUTES.AUDIT,
    activeIcon: <Icon name="audit-log" size="md" />,
  },
  {
    divider: true,
    label: AI_BRAND_NAME,
    icon: <AtlasAIIcon />,
    activeIcon: <AtlasAIIconActive />,
    key: ROUTES.ATLAS_AI,
    link: ROUTES.ATLAS_AI,
  },
]

export const SIDE_MENU_UW_USER: IMenuData[] = [
  {
    label: 'Dashboard',
    icon: <Icon name="dashboard" size="md" />,
    link: ROUTES.DUE_DILIGENCE_HOME,
    key: ROUTES.DUE_DILIGENCE_HOME,
  },
  MY_TASKS_MENU_ITEM,
  MESSAGES_MENU_ITEM,
  {
    label: 'Operations',
    icon: <Icon name="activity-box" size="md" />,
    key: ROUTES.ACTIVITY_QUEUE,
    link: ROUTES.ACTIVITY_QUEUE,
  },
  {
    label: 'Collections',
    link: ROUTES.COLLECTIONS_DASHBOARD,
    key: ROUTES.COLLECTIONS_DASHBOARD,
    icon: <Icon name="collections" size="md" />,
  },
  {
    label: 'Clients',
    icon: <Icon name="people" size="md" />,
    key: ROUTES.CLIENT_MANAGEMENT,
    link: ROUTES.CLIENT_MANAGEMENT,
    activeIcon: <Icon name="people-active" size="md" />,
  },
  {
    label: 'Prospects',
    icon: <Icon name="prospects" size="md" />,
    key: ROUTES.PROSPECTS,
    link: ROUTES.PROSPECTS,
  },
  REPORTS_MENU_ITEM,
  {
    label: 'CRM',
    icon: <Icon name="briefcase" size="md" />,
    key: ROUTES.CRM_HOME,
    link: ROUTES.CRM_HOME,
  },
  {
    divider: true,
    label: AI_BRAND_NAME,
    icon: <AtlasAIIcon />,
    activeIcon: <AtlasAIIconActive />,
    key: ROUTES.ATLAS_AI,
    link: ROUTES.ATLAS_AI,
  },
]

export const SIDE_MENU_CLIENT_USER: IMenuData[] = [
  {
    label: 'Dashboard',
    icon: <Icon name="dashboard" size="md" />,
    key: ROUTES.HOMEPAGE,
    link: ROUTES.HOMEPAGE,
  },
  {
    label: 'Activity',
    icon: <Icon name="activity" size="md" />,
    link: ROUTES.ACCOUNT_ACTIVITY,
    key: ROUTES.ACCOUNT_ACTIVITY,
  },
  {
    label: 'Reporting',
    icon: <Icon name="reporting" size="md" />,
    key: ROUTES.SUBMIT_REPORTING,
    link: ROUTES.SUBMIT_REPORTING,
  },
  {
    label: 'Analytics',
    icon: <Icon name="chart-bars" size="md" />,
    key: ROUTES.CLIENT_VISUALIZATIONS,
    link: ROUTES.CLIENT_VISUALIZATIONS,
  },
  {
    label: 'Settings',
    icon: <ClientSettings />,
    key: ROUTES.SETTINGS,
    link: ROUTES.SETTINGS,
  },
]

export const SIDE_MENU_PAST_CLIENT_USER: IMenuData[] = [
  {
    label: 'Activity',
    icon: <Icon name="activity" size="md" />,
    link: ROUTES.HOMEPAGE,
    key: ROUTES.HOMEPAGE,
  },
  {
    label: 'Settings',
    icon: <ClientSettings />,
    key: ROUTES.SETTINGS,
    link: ROUTES.SETTINGS,
  },
]

export const SIDE_MENU_BDO_USER: IMenuData[] = [
  {
    label: 'Dashboard',
    icon: <Icon name="dashboard" size="md" />,
    key: ROUTES.PROSPECTS,
    link: ROUTES.PROSPECTS,
  },
  MY_TASKS_MENU_ITEM,
  MESSAGES_MENU_ITEM,
  {
    label: 'CRM',
    icon: <Icon name="briefcase" size="md" />,
    key: ROUTES.CRM_HOME,
    link: ROUTES.CRM_HOME,
  },
  REPORTS_MENU_ITEM,
  {
    label: 'Diligence',
    icon: <Icon name="underwriting" size="md" />,
    key: ROUTES.DUE_DILIGENCE_HOME,
    link: ROUTES.DUE_DILIGENCE_HOME,
  },
  {
    divider: true,
    label: AI_BRAND_NAME,
    icon: <AtlasAIIcon />,
    activeIcon: <AtlasAIIconActive />,
    key: ROUTES.ATLAS_AI,
    link: ROUTES.ATLAS_AI,
  },
]

export const SIDE_MENU_PARTICIPANTS_USER: IMenuData[] = [
  {
    label: 'Clients',
    icon: <Icon name="people" size="md" />,
    link: ROUTES.CLIENT_MANAGEMENT,
    key: ROUTES.CLIENT_MANAGEMENT,
    activeIcon: <Icon name="people-active" size="md" />,
  },
  {
    label: 'Wires',
    icon: <Icon name="transfer" size="md" />,
    key: ROUTES.OUTGOING_WIRE,
    link: ROUTES.OUTGOING_WIRE,
  },
]

/**
 * Tasks and AIM, and nothing else.
 *
 * The whole point of the tasks-and-messaging role: two destinations, so the rail
 * has two items. Everything else in the portal is denied server-side for this
 * role, and PrivateRoute sends a typed-in URL back here rather than rendering a
 * page whose every request would 403.
 *
 * Both items badge: `unreadTasks` and `unreadMessages` are seeded in MainLayout
 * for whoever has each surface, which for this role is both.
 */
export const SIDE_MENU_TASKS_AND_MESSAGING: IMenuData[] = [MY_TASKS_MENU_ITEM, MESSAGES_MENU_ITEM]

const SIDE_MENU: Record<UserRole, IMenuData[]> = {
  [UserRole.ADMIN]: SIDE_MENU_ADMIN,
  [UserRole.PORTFOLIO_ADMIN]: SIDE_MENU_PORTFOLIO_USER,
  [UserRole.PORTFOLIO_USER]: SIDE_MENU_PORTFOLIO_USER,
  [UserRole.UW_USER]: SIDE_MENU_UW_USER,
  [UserRole.CLIENT_USER]: [],
  [UserRole.BDO]: SIDE_MENU_BDO_USER,
  [UserRole.PARTICIPANT_USER]: SIDE_MENU_PARTICIPANTS_USER,
  [UserRole.BOT_USER]: [],
  [UserRole.TASKS_AND_MESSAGING]: SIDE_MENU_TASKS_AND_MESSAGING,
} as const

export { SIDE_MENU }
